import { Link } from 'react-router-dom'
// FaShip 已随「散货拼柜」卡片下线一并移出；恢复时需重新加回本行。
import { FaGlobe, FaCalculator, FaTruck, FaMapMarkerAlt, FaHeadset, FaCheck, FaArrowRight, FaShieldAlt, FaClock, FaUsers } from 'react-icons/fa'
import { useInView, useTypewriter, useStaggerAnimation } from '../hooks/useAnimations'

const stats = [
  { number: '200+', label: '覆盖国家/地区' },
  { number: '50K+', label: '累计服务客户' },
  { number: '99.5%', label: '准时送达率' },
  { number: '24h', label: '客服响应' },
]

const services = [
  {
    icon: FaCalculator,
    title: '运费估算',
    description: '智能比价系统，一键获取空运、海运、快递等多渠道报价，透明无隐藏费用',
    features: ['多渠道实时比价', '时效费用一目了然', '支持精准到门报价'],
    link: '/freight-calculator',
    linkText: '立即估算',
  },
  {
    icon: FaTruck,
    title: '集运报名',
    description: '专业集运团队，一键提交需求，专人对接，全程可视化追踪您的货物状态',
    features: ['一键快捷报名', '专属客服对接', '实时状态更新'],
    link: '/consolidation',
    linkText: '立即报名',
    featured: true,
  },
  // 散货板块已下线（2026-07-30），注释保留。详见 components/layout/Navbar.jsx 的说明。
  // {
  //   icon: FaShip,
  //   title: '散货拼柜',
  //   description: '灵活拼箱方案，海运空运任选，费用透明合理，适合中小批量货物运输',
  //   features: ['灵活拼箱方案', '费用结构透明', '适合中小批量'],
  //   link: '/lcl',
  //   linkText: '了解详情',
  // },
]

const features = [
  {
    icon: FaMapMarkerAlt,
    title: '全程可追踪',
    description: '从发货到签收，每一个环节都清晰可查，让您安心无忧',
  },
  {
    icon: FaHeadset,
    title: '专业客服',
    description: '7x24小时在线支持，专业物流顾问一对一服务',
  },
  {
    icon: FaShieldAlt,
    title: '安全保障',
    description: '货物全程保险保障，补偿机制完善，让您运输无忧',
  },
  {
    icon: FaClock,
    title: '准时送达',
    description: '严格时效管理，多年经验积累，99.5%准时送达率',
  },
]

export default function HomePage() {
  // Scroll-triggered visibility refs
  const [servicesRef, servicesInView] = useInView()
  const [featuresRef, featuresInView] = useInView()
  const [ctaRef, ctaInView] = useInView()

  // Typewriter effects for section titles
  const servicesTitle = useTypewriter('专业的跨境物流服务，满足您的多样化需求', servicesInView, 100)
  const featuresTitle = useTypewriter('专业团队、完善服务、贴心保障，让您的跨境物流更简单', featuresInView, 86)

  // Staggered animations for cards
  const serviceCardVisible = useStaggerAnimation(services.length, servicesInView, 200)
  const featureCardVisible = useStaggerAnimation(features.length, featuresInView, 150)

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-700 via-primary-800 to-gray-900 text-white py-20 lg:py-28 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="container relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6 animate-fade-in-up">
            <FaGlobe className="text-primary-300" />
            <span>全球物流服务商</span>
          </div>
          
          <h1
            className="text-fluid-4xl lg:text-5xl xl:text-6xl font-bold mb-6 leading-tight animate-fade-in-up delay-200 bg-gradient-to-r from-white to-primary-400 bg-clip-text text-transparent"
            style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
          >
            智慧物流，使命必达
          </h1>
          
          <p className="text-lg lg:text-xl text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-400">
            专业跨境物流一站式服务平台，整合空运、海运、快递多渠道资源<br className="hidden lg:block" />
            为您提供高效、透明、可追溯的全链路物流解决方案
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in-up delay-600">
            <Link
              to="/freight-calculator"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 font-semibold rounded-lg hover:bg-primary-50 transition-all duration-200 shadow-lg hover:shadow-xl animate-pulse-glow"
            >
              <FaCalculator />
              运费估算
            </Link>
            <Link
              to="/consolidation"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/60 text-white font-semibold rounded-lg hover:bg-white/10 hover:border-white transition-all duration-200 animate-gentle-float"
              style={{ animationDelay: '0.5s' }}
            >
              <FaTruck />
              集运报名
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-fluid-xs gap-6 lg:gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className={`text-center animate-fade-in-up`} style={{ animationDelay: `${0.8 + index * 0.15}s` }}>
                <div className="text-fluid-3xl lg:text-4xl font-bold mb-1">{stat.number}</div>
                <div className="text-white/70 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white" ref={servicesRef}>
        <div className="container">
          <h2 className={`text-fluid-3xl font-bold text-center mb-4 transition-opacity duration-500 ${servicesInView ? 'opacity-100' : 'opacity-0'}`}>
            核心服务
          </h2>
          <p className={`text-gray-500 text-center mb-12 max-w-2xl mx-auto transition-all duration-700 delay-300 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span>{servicesTitle}</span>
          </p>

          <div className="grid grid-cols-fluid-lg gap-8">
            {services.map((service, index) => {
              const Icon = service.icon
              return (
                <div
                  key={index}
                  className={`relative bg-white rounded-xl p-8 border transition-all duration-500 hover:shadow-xl ${
                    service.featured
                      ? 'border-primary-200 shadow-lg ring-2 ring-primary-100'
                      : 'border-gray-200 shadow hover:border-primary-200'
                  } ${serviceCardVisible[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  {service.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                      热门
                    </div>
                  )}
                  
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                    service.featured ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Icon className="text-2xl" />
                  </div>
                  
                  <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-500 text-sm mb-4 leading-relaxed">{service.description}</p>
                  
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <FaCheck className="text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to={service.link}
                    className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 transition-colors"
                  >
                    {service.linkText}
                    <FaArrowRight className="text-sm" />
                  </Link>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50" ref={featuresRef}>
        <div className="container">
          <h2 className={`text-fluid-3xl font-bold text-center mb-4 transition-opacity duration-500 ${featuresInView ? 'opacity-100' : 'opacity-0'}`}>
            为什么选择我们
          </h2>
          <p className={`text-gray-500 text-center mb-12 max-w-2xl mx-auto transition-all duration-700 delay-300 ${featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span>{featuresTitle}</span>
          </p>

          <div className="grid grid-cols-fluid-lg gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div
                  key={index}
                  className={`bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-all duration-500 ${
                    featureCardVisible[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-xl" />
                  </div>
                  <h4 className="font-semibold mb-2">{feature.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-700" ref={ctaRef}>
        <div className={`container text-center transition-all duration-700 ${ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-fluid-3xl font-bold text-white mb-4">开始您的跨境物流之旅</h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            立即注册，享受专业的跨境物流服务，让您的货物安全、快捷送达全球
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 font-semibold rounded-lg hover:bg-primary-50 transition-all duration-200"
            >
              <FaUsers />
              免费注册
            </Link>
            <Link
              to="/tracking"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/60 text-white font-semibold rounded-lg hover:bg-white/10 hover:border-white transition-all duration-200"
            >
              <FaMapMarkerAlt />
              查询运单
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
